import * as React from "react";
import { useSelector } from "@teamprevent/admin-redux";
import { Loader, Container } from "semantic-ui-react";

import { useDispatch } from "@teamprevent/admin-redux";
import { CompletedDate } from "@teamprevent/common/components/CompletedDate";
import { ContextAwareProgressBarQuestionnaireContainer } from "@teamprevent/common/questionnaires/components/ContextAwareProgressBarQuestionnaireContainer";
import { AnsweredQuestionWithId, QuestionnaireTypes } from "@teamprevent/common/questionnaires/model";
import { CustomQuestionConfig } from "@teamprevent/common/questionnaires/questionnaireConfig";
import * as questionIds from "@teamprevent/common/healthSurveillance/questions/questionIds";
import { navigateToParentRoute } from "@teamprevent/common/routing/actions";
import { submitInvalidQuestionnaire as submitInvalidQuestionnaireAction } from "@teamprevent/common/questionnaires/actions";

import { useCustomMedicalTypes } from "../../customMedicalTypes/hooks/useCustomMedicalTypes";
import { DidNotAttendButton } from "../../clinicalNotes/components/DidNotAttendButton";
import { ApplicationState } from "../../applicationState";
import { taskSelector } from "../../task/selectors";

import {
    loadHavsClinicalAssessmentOfHistoryAndExamination,
    saveHavsClinicalAssessmentOfHistoryAndExamination,
    submitHavsClinicalAssessmentOfHistoryAndExamination
} from "../actions";

import { useMedicalTypesForOhCase } from "../hooks/useMedicalTypesForOhCase";

import { questionConfigsForConsultationSelector } from "../selectors";

import { HavsClinicalAssessmentOfHistoryAndExaminationSection } from "./HavsClinicalAssessmentOfHistoryAndExaminationSection";

export const HavsClinicalAssessmentOfHistoryAndExaminationKey = "HavsClinicalAssessmentOfHistoryAndExamination";

type Props = {
    appointmentId?: string;
    readonly: boolean;
};

export const HavsClinicalAssessmentOfHistoryAndExaminationQuestions: React.FunctionComponent<Props> = ({
    appointmentId,
    readonly
}: Props) => {
    const dispatch = useDispatch();

    const customerId = useSelector((state: ApplicationState) => state.router.params!.customerId);
    const ohCaseId = useSelector((state: ApplicationState) => state.router.params!.ohCaseId);
    const taskId = useSelector((state: ApplicationState) => state.router.params!.taskId);
    const task = useSelector(taskSelector);

    const {
        customMedicalTypes,
        refetch: loadCustomMedicalTypes
    } = useCustomMedicalTypes({ enable: false });

    const {
        medicalTypes: medicalTypesForQuestionnaire,
        refetch: loadMedicalTypesForQuestionnaire
    } = useMedicalTypesForOhCase({ customerId, ohCaseId }, { enable: false });

    const navigateToNextPage = React.useCallback(() => dispatch(navigateToParentRoute()), [dispatch]);

    const submitInvalidQuestionnaire = React.useCallback(
        () => dispatch(submitInvalidQuestionnaireAction(HavsClinicalAssessmentOfHistoryAndExaminationKey)),
        [dispatch]);

    const submitQuestionnaire = React.useCallback(async (submittedQuestions: AnsweredQuestionWithId[]): Promise<boolean> => {
        const trimAnswerAndUpdateEmployeeQuestion = (questionId: string, questions: AnsweredQuestionWithId[]) => {

            const question = questions.find(q => q.id === questionId);

            if (question) {
                question.answer = question.answer?.toString()?.trim();
            }
        };

        trimAnswerAndUpdateEmployeeQuestion(questionIds.Title, submittedQuestions);
        trimAnswerAndUpdateEmployeeQuestion(questionIds.FirstName, submittedQuestions);
        trimAnswerAndUpdateEmployeeQuestion(questionIds.LastName, submittedQuestions);

        if (submittedQuestions.some(q => q.isValid !== true && q.isRemovedByUser !== true)) {
            submitInvalidQuestionnaire();
            return false;
        } else {
            const submitted = await dispatch(submitHavsClinicalAssessmentOfHistoryAndExamination(
                ohCaseId,
                taskId));

            navigateToNextPage();
            return submitted as boolean;
        }
    }, [dispatch, navigateToNextPage, ohCaseId, submitInvalidQuestionnaire, taskId]);

    const [questions, setQuestions] = React.useState<AnsweredQuestionWithId[] | null>(null);

    const getQuestionnaire = React.useCallback(
        (abort: AbortController) => dispatch(loadHavsClinicalAssessmentOfHistoryAndExamination(ohCaseId, taskId, abort)),
        [dispatch, ohCaseId, taskId]);

    const loadQuestionnaire = React.useCallback(async (abort: AbortController) => {
        setQuestions(null);
        const result = await getQuestionnaire(abort);
        setQuestions(result);
        await Promise.all([
            loadCustomMedicalTypes(),
            loadMedicalTypesForQuestionnaire()
        ]);
    }, [getQuestionnaire, loadCustomMedicalTypes, loadMedicalTypesForQuestionnaire]);

    const questionConfigs = React.useMemo<CustomQuestionConfig[] | null | undefined>(() => {
        if (readonly === true) {
            return questions?.map(q => ({ id: q.id, questionText: q.question }));
        } else if (readonly === false && medicalTypesForQuestionnaire && customMedicalTypes) {
            return questionConfigsForConsultationSelector(medicalTypesForQuestionnaire, customMedicalTypes);
        }

        return null;
    }, [medicalTypesForQuestionnaire, questions, readonly, customMedicalTypes]);

    const saveQuestionnaire = React.useCallback((qs: AnsweredQuestionWithId[], eTag: string | null) =>
        dispatch(saveHavsClinicalAssessmentOfHistoryAndExamination(ohCaseId, taskId, qs, eTag)),
    [dispatch, ohCaseId, taskId]);

    if (!readonly && ((!customerId || !ohCaseId || !taskId) && !appointmentId)) {
        return <Loader active />;
    }

    return (
        <>
            {(!readonly && !appointmentId) && <Container textAlign="right" className="dna-button-container">
                <DidNotAttendButton
                    customerId={customerId}
                    ohCaseId={ohCaseId}
                    task={task}
                />
            </Container>}
            <CompletedDate readonly={readonly} task={task} />
            <ContextAwareProgressBarQuestionnaireContainer
                readonly={readonly}
                autoSaveQuestionnaire={saveQuestionnaire}
                loadQuestionnaire={loadQuestionnaire}
                submitQuestionnaire={submitQuestionnaire}
                questionnaireKey={HavsClinicalAssessmentOfHistoryAndExaminationKey}
                questionnaireType={QuestionnaireTypes.Other}
            >
                <HavsClinicalAssessmentOfHistoryAndExaminationSection
                    questionConfigs={questionConfigs} />
            </ContextAwareProgressBarQuestionnaireContainer>
        </>
    );
};
